import * as Schema from "effect/Schema";

// NV5 — unseen settle: "did anything finish here after I last stood in this
// World?". Liveness answers a question about THE WORK and rightly excludes
// settled threads (the membership rule); this fold answers a question about
// THE PERSON, so it is kept as its own named thing (DL2 — kinds distinct;
// see also the per-thread lastVisitedAt machinery in rootSessionStripContainer,
// which answers a third question, "did I open that thread", and is not this).
//
// Inputs are RAW shell stamps, never the strip adapter's EFFECTIVE lifecycle
// fields: the adapter substitutes a minute-coarsened `now` for auto-settled
// threads, which against a stored cursor would invent a fresh "unseen" every
// minute. A raw `settledAt` is the server's one thread.settled stamp — a
// deliberate completion at a real moment. Auto-settles (inactivity folds with
// no stamp) are deliberately not this marker's business: their moment was the
// person watching the thread go idle, not a completion arriving while away.
//
// The stored half is ONLY the seen cursor — when the person last stood in
// each door (device-local view state, the rail cap's door-fact custody).
// Everything else is computed per render and persisted nowhere (DL3/DL4):
// the fold changes what a door LOOKS like, never what a door IS. A door with
// no cursor yet shows nothing — absence is typed, never invented into a
// marker for work that settled before this device ever stood there.

/** One thread's contribution, structurally narrowed from the raw shell. */
export interface UnseenSettleThreadInput {
  readonly worldKey: string | null;
  readonly archivedAt: string | null;
  readonly settledAt: string | null;
}

export const WorldSeenCursorsSchema = Schema.Struct({
  byWorldKey: Schema.Record(Schema.String, Schema.String),
  local: Schema.NullOr(Schema.String),
});

/** When the person last stood in each door, ISO-8601. Null local = never. */
export type WorldSeenCursors = typeof WorldSeenCursorsSchema.Type;

export const EMPTY_WORLD_SEEN_CURSORS: WorldSeenCursors = {
  byWorldKey: {},
  local: null,
};

/** The one localStorage doorway for the cursors (device view state, FD07). */
export const WORLD_SEEN_CURSORS_STORAGE_KEY = "cleo:neucleos:world-seen-cursors:v1";

export interface WorldUnseenSettleRollups {
  /** Doors with a settle the person has not stood in the World since. */
  readonly byWorldKey: ReadonlySet<string>;
  readonly local: boolean;
}

export const EMPTY_WORLD_UNSEEN_SETTLE: WorldUnseenSettleRollups = {
  byWorldKey: new Set<string>(),
  local: false,
};

const settledAfter = (settledAt: string, cursor: string | undefined | null): boolean => {
  if (cursor == null) return false;
  const settled = Date.parse(settledAt);
  const seen = Date.parse(cursor);
  // An unparseable stamp marks nothing: a marker must never rest on a guess.
  if (Number.isNaN(settled) || Number.isNaN(seen)) return false;
  return settled > seen;
};

/**
 * Fold raw thread stamps against the seen cursors. Membership: a non-archived
 * thread with a real settle stamp; archived threads are deliberately silent
 * (the person put them away) exactly as they are for liveness.
 */
export function projectUnseenSettleRollups(input: {
  readonly threads: ReadonlyArray<UnseenSettleThreadInput>;
  readonly cursors: WorldSeenCursors;
}): WorldUnseenSettleRollups {
  const byWorldKey = new Set<string>();
  let local = false;
  for (const thread of input.threads) {
    if (thread.settledAt === null || thread.archivedAt !== null) continue;
    if (thread.worldKey === null) {
      local = local || settledAfter(thread.settledAt, input.cursors.local);
      continue;
    }
    if (byWorldKey.has(thread.worldKey)) continue;
    if (settledAfter(thread.settledAt, input.cursors.byWorldKey[thread.worldKey])) {
      byWorldKey.add(thread.worldKey);
    }
  }
  return { byWorldKey, local };
}

/** The door being stamped: a World's key, or the Local (ungoverned) door. */
export type SeenCursorDoor =
  | { readonly kind: "world"; readonly worldKey: string }
  | { readonly kind: "local" };

/** Pure stamp: the given doors were stood in at `now`. */
export function stampSeenCursors(
  cursors: WorldSeenCursors,
  doors: ReadonlyArray<SeenCursorDoor>,
  now: string,
): WorldSeenCursors {
  if (doors.length === 0) return cursors;
  const byWorldKey: Record<string, string> = { ...cursors.byWorldKey };
  let local = cursors.local;
  for (const door of doors) {
    if (door.kind === "local") local = now;
    else byWorldKey[door.worldKey] = now;
  }
  return { byWorldKey, local };
}
